import classNames from "classnames";
import { PropsWithChildren, ReactNode, useId } from "react";
import { FieldError } from "react-hook-form";
import { useTranslations } from "../../hooks/useTranslations";
import styles from "./FormField.module.css";

interface Props {
  label?: string;
  children:
    | ReactNode
    | ((props: {
        labelId: string;
        isOptional: boolean;
        disabled: boolean;
        isInvalid: boolean;
      }) => ReactNode);
  helpText?: ReactNode;
  isOptional?: boolean;
  alignItemsRight?: boolean;
  disabled?: boolean;
  error?: FieldError;
  direction?: "column" | "row";
  className?: string;
}

const FormField = ({
  label,
  helpText,
  children,
  isOptional = false,
  alignItemsRight = false,
  disabled = false,
  error,
  direction,
  className,
}: Props) => {
  const { i18n } = useTranslations();
  const labelId = useId();
  const isInvalid = error !== undefined;

  return (
    <div
      className={classNames(styles.field, className, {
        [styles.disabled]: disabled,
        [styles.column]: direction === "column",
        [styles.row]: direction === "row",
      })}
    >
      {label && (
        <div className={styles.leftColumn}>
          <label htmlFor={labelId} className={styles.label}>
            {label}
            {isOptional && (
              <span className={styles.optional}>{i18n.formField.optional}</span>
            )}
          </label>
        </div>
      )}
      <div
        className={classNames(styles.rightColumn, {
          [styles.column]: direction === "column",
          [styles.alignRight]: alignItemsRight,
        })}
      >
        {typeof children === "function"
          ? children({
              labelId,
              isOptional,
              disabled,
              isInvalid,
            })
          : children}
        {isInvalid && <Error error={error} />}
        {helpText && <Help>{helpText}</Help>}
      </div>
    </div>
  );
};

const Help = ({ children }: PropsWithChildren<{}>) => (
  <p className={styles.helpText}>{children}</p>
);

export const Error = ({ error }: { error: FieldError }) => {
  const { i18n } = useTranslations();
  return <p className={styles.error}>{getErrorMessage(error, i18n)}</p>;
};

const getErrorMessage = (
  { message, type }: FieldError,
  i18n: ReturnType<typeof useTranslations>["i18n"],
): string => {
  if (message) return message;

  switch (type) {
    case "required":
      return i18n.formField.errors.required;
    case "min":
      return i18n.formField.errors.min;
    case "max":
      return i18n.formField.errors.max;
    case "pattern":
      return i18n.formField.errors.pattern;
    case "validate":
      return i18n.formField.errors.validate;
    default:
      return "";
  }
};

FormField.Error = Error;
FormField.Help = Help;

export default FormField;
